import React, { useState, useEffect, useCallback } from "react";
import {
  CheckSquare,
  Square,
  Clock,
  User,
  Calendar,
  Paperclip,
  ChevronDown,
  ChevronRight,
  Filter,
  Search,
  AlertTriangle,
  MapPin,
  FileText,
  Upload,
  Loader2,
  MoreHorizontal,
  Check,
} from "lucide-react";
import { taskPipelineApi, tasksApi, usersApi, documentsApi } from "../../services/api";
import { motion, AnimatePresence } from "framer-motion";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";

// ─── Types ──────────────────────────────────────────────────────────────────────

interface Props {
  matterId: string;
  reCase: any;
}

// ─── Constants ──────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { key: "intake", label: "Intake", icon: FileText },
  { key: "due_diligence", label: "Due Diligence", icon: Search },
  { key: "requisitions", label: "Requisitions", icon: FileText },
  { key: "signing", label: "Signing", icon: FileText },
  { key: "pre_closing", label: "Pre-Closing", icon: Clock },
  { key: "closing_day", label: "Closing Day", icon: Calendar },
  { key: "post_closing", label: "Post-Closing", icon: CheckSquare },
  { key: "reporting", label: "Reporting / Archive", icon: FileText },
];

const JURISDICTION_OPTIONS = [
  { value: "all", label: "All Jurisdictions" },
  { value: "ON", label: "Ontario" },
  { value: "Toronto", label: "Toronto" },
  { value: "Global", label: "Global" },
];

// ─── Helpers ────────────────────────────────────────────────────────────────────

const getInitials = (name: string) =>
  name
    ?.split(" ")
    .slice(0, 2)
    .map((w: string) => w[0]?.toUpperCase())
    .join("") || "?";

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString("en-CA", { month: "short", day: "numeric" });

const isOverdue = (d: string) => new Date(d) < new Date() && !!d;

const priorityColor = (p: string) =>
  ({
    urgent: "bg-red-100 text-red-700",
    high: "bg-orange-100 text-orange-700",
    normal: "bg-slate-100 text-slate-600",
    low: "bg-slate-50 text-slate-400",
  }[p] || "bg-slate-100 text-slate-600");

// ─── Component ──────────────────────────────────────────────────────────────────

const RealEstateChecklistTab: React.FC<Props> = ({ matterId, reCase }) => {
  const [tasks, setTasks] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(() =>
    CATEGORIES.reduce((acc, c) => ({ ...acc, [c.key]: true }), {} as Record<string, boolean>)
  );

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState<string | null>(null);
  const [showCompleted, setShowCompleted] = useState(true);
  const [jurisdictionFilter, setJurisdictionFilter] = useState<string | null>("all");

  // ─── Fetch tasks ──────────────────────────────────────────────────────────────

  const fetchTasks = useCallback(async () => {
    try {
      const res = await taskPipelineApi.getByMatter(matterId);
      const allTasks: any[] = [];
      Object.values(res.data).forEach((stageTasks: any) => {
        if (Array.isArray(stageTasks)) {
          allTasks.push(...stageTasks);
        }
      });
      setTasks(allTasks);
    } catch (err) {
      console.error("Failed to fetch pipeline tasks:", err);
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  // ─── Fetch users ─────────────────────────────────────────────────────────────

  const fetchUsers = useCallback(async () => {
    try {
      const res = await usersApi.list();
      setUsers(res.data?.users || res.data || []);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, [fetchTasks, fetchUsers]);

  // ─── Toggle task completion ───────────────────────────────────────────────────

  const toggleComplete = useCallback(
    async (task: any) => {
      const newStatus = task.status === "done" ? "todo" : "done";
      // Optimistic update
      setTasks((prev) =>
        prev.map((t) => (t._id === task._id ? { ...t, status: newStatus } : t))
      );
      try {
        await tasksApi.changeStatus(task._id, newStatus);
      } catch {
        fetchTasks(); // rollback on failure
      }
    },
    [fetchTasks]
  );

  // ─── Toggle category expand ───────────────────────────────────────────────────

  const toggleCategory = useCallback((key: string) => {
    setExpandedCategories((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // ─── Filter tasks ─────────────────────────────────────────────────────────────

  const filteredTasks = tasks.filter((task) => {
    // Search filter
    if (
      searchQuery &&
      !task.title?.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }
    // Assignee filter
    if (assigneeFilter) {
      const assigneeIds = (task.assigneeUserIds || []).map((u: any) =>
        typeof u === "string" ? u : u._id
      );
      if (!assigneeIds.includes(assigneeFilter)) {
        return false;
      }
    }
    // Show completed toggle
    if (!showCompleted && task.status === "done") {
      return false;
    }
    // Jurisdiction filter
    if (jurisdictionFilter && jurisdictionFilter !== "all") {
      const scope = task.jurisdictionScope?.value;
      if (scope && scope !== jurisdictionFilter) {
        return false;
      }
    }
    return true;
  });

  // ─── Group tasks by category ──────────────────────────────────────────────────

  const groupedTasks = CATEGORIES.map((cat) => {
    const catTasks = filteredTasks.filter((t) => t.category === cat.key);
    const allCatTasks = tasks.filter((t) => t.category === cat.key);
    const completed = allCatTasks.filter((t) => t.status === "done").length;
    return {
      ...cat,
      tasks: catTasks,
      total: allCatTasks.length,
      completed,
    };
  });

  // ─── Summary stats ────────────────────────────────────────────────────────────

  const totalTasks = tasks.length;
  const completedTasks = tasks.filter((t) => t.status === "done").length;
  const overdueTasks = tasks.filter(
    (t) => t.status !== "done" && t.dueAt && isOverdue(t.dueAt)
  ).length;
  const progressPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  // ─── User options for SearchableSelect ────────────────────────────────────────

  const userOptions: SelectOption[] = users.map((u: any) => ({
    value: u._id,
    label: u.name || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email,
  }));

  // ─── Loading state ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        <span className="ml-2 text-sm text-slate-500">Loading checklist...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ─── Template Info Banner ─────────────────────────────────────────────── */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-800">
            Auto-generated checklist
          </p>
          <p className="text-xs text-blue-600 mt-0.5">
            These tasks were auto-generated from the{" "}
            {reCase?.jurisdiction || "Ontario"} program template. Review and
            adjust as needed for your specific transaction.
          </p>
        </div>
      </div>

      {/* ─── Summary Stats ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
          <p className="text-2xl font-bold text-slate-800">{totalTasks}</p>
          <p className="text-xs text-slate-500">Total Tasks</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">{completedTasks}</p>
          <p className="text-xs text-slate-500">Completed</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
          <p className="text-2xl font-bold text-amber-600">{overdueTasks}</p>
          <p className="text-xs text-slate-500">Overdue</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
          <p className="text-2xl font-bold text-blue-600">{progressPct}%</p>
          <p className="text-xs text-slate-500">Progress</p>
        </div>
      </div>

      {/* ─── Filter Bar ───────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm rounded-xl border border-slate-200 focus:outline-none focus:border-emerald-600"
            />
          </div>

          {/* Assignee filter */}
          <div className="w-48">
            <SearchableSelect
              options={userOptions}
              value={assigneeFilter}
              onChange={setAssigneeFilter}
              placeholder="All Assignees"
              icon={User}
              clearable
            />
          </div>

          {/* Jurisdiction filter */}
          <div className="w-44">
            <SearchableSelect
              options={JURISDICTION_OPTIONS}
              value={jurisdictionFilter}
              onChange={(v) => setJurisdictionFilter(v || "all")}
              placeholder="Jurisdiction"
              icon={MapPin}
            />
          </div>

          {/* Show completed toggle */}
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            className={`flex items-center gap-2 px-3 py-2 text-sm rounded-xl border transition-colors ${
              showCompleted
                ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
            }`}
          >
            {showCompleted ? (
              <CheckSquare className="h-4 w-4" />
            ) : (
              <Square className="h-4 w-4" />
            )}
            <span>Completed</span>
          </button>
        </div>
      </div>

      {/* ─── Category Groups ──────────────────────────────────────────────────── */}
      <div className="space-y-3">
        <AnimatePresence initial={false}>
          {groupedTasks.map((group) => {
            // Skip empty categories when filtering
            if (group.tasks.length === 0 && group.total === 0) return null;

            const pct =
              group.total > 0
                ? Math.round((group.completed / group.total) * 100)
                : 0;
            const expanded = expandedCategories[group.key] ?? true;
            const GroupIcon = group.icon;

            return (
              <motion.div
                key={group.key}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
              >
                {/* Category header */}
                <button
                  onClick={() => toggleCategory(group.key)}
                  className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <GroupIcon className="h-4 w-4 text-slate-500" />
                    <span className="font-semibold text-slate-800">
                      {group.label}
                    </span>
                    <span className="text-xs text-slate-400">
                      {group.completed}/{group.total}
                    </span>
                    {/* Progress bar mini */}
                    <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                  <ChevronDown
                    className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${
                      expanded ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Expanded task list */}
                <AnimatePresence initial={false}>
                  {expanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-slate-100 overflow-hidden"
                    >
                      {group.tasks.length === 0 ? (
                        <div className="px-5 py-6 text-center">
                          <p className="text-sm text-slate-400">
                            {group.total === 0
                              ? "No tasks in this category"
                              : "All tasks hidden by current filters"}
                          </p>
                        </div>
                      ) : (
                        group.tasks.map((task: any) => (
                          <ChecklistItem
                            key={task._id}
                            task={task}
                            users={users}
                            onToggle={toggleComplete}
                          />
                        ))
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* ─── Empty state ──────────────────────────────────────────────────────── */}
      {totalTasks === 0 && !loading && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-slate-100 mb-4">
            <CheckSquare className="h-6 w-6 text-slate-400" />
          </div>
          <p className="text-sm font-medium text-slate-700">
            No checklist tasks yet
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Tasks will appear here once a pipeline template is applied to this
            matter.
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Checklist Item ─────────────────────────────────────────────────────────────

interface ChecklistItemProps {
  task: any;
  users: any[];
  onToggle: (task: any) => void;
}

const ChecklistItem: React.FC<ChecklistItemProps> = ({ task, users, onToggle }) => {
  // Resolve assignee names
  const assignees = (task.assigneeUserIds || []).map((ref: any) => {
    if (typeof ref === "object" && ref.name) return ref;
    const found = users.find((u: any) => u._id === ref);
    return found || { _id: ref, name: "Unknown" };
  });

  return (
    <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-50 last:border-b-0 hover:bg-slate-50/50 group transition-colors">
      {/* Checkbox */}
      <button
        onClick={() => onToggle(task)}
        className="flex-shrink-0 focus:outline-none"
      >
        {task.status === "done" ? (
          <CheckSquare className="h-5 w-5 text-emerald-500" />
        ) : (
          <Square className="h-5 w-5 text-slate-300 group-hover:text-slate-400 transition-colors" />
        )}
      </button>

      {/* Title + metadata */}
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-medium truncate ${
            task.status === "done"
              ? "text-slate-400 line-through"
              : "text-slate-800"
          }`}
        >
          {task.title}
        </p>
        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
          {/* Due date */}
          {task.dueAt && (
            <span
              className={`text-xs flex items-center gap-1 ${
                task.status !== "done" && isOverdue(task.dueAt)
                  ? "text-red-500"
                  : "text-slate-400"
              }`}
            >
              <Calendar className="h-3 w-3" />
              {formatDate(task.dueAt)}
            </span>
          )}
          {/* Jurisdiction scope badge */}
          {task.jurisdictionScope?.value && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-600">
              {task.jurisdictionScope.value}
            </span>
          )}
          {/* Evidence required indicator */}
          {task.requiresEvidence && (
            <span
              className={`text-xs flex items-center gap-1 ${
                task.evidenceDocumentIds?.length > 0
                  ? "text-emerald-500"
                  : "text-amber-500"
              }`}
            >
              <Paperclip className="h-3 w-3" />
              {task.evidenceDocumentIds?.length > 0
                ? "Evidence attached"
                : "Evidence required"}
            </span>
          )}
          {/* Stage badge */}
          {task.pipelineStage && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
              {task.pipelineStage}
            </span>
          )}
        </div>
      </div>

      {/* Assignee avatars */}
      {assignees.length > 0 && (
        <div className="flex -space-x-1.5 flex-shrink-0">
          {assignees.slice(0, 3).map((u: any) => (
            <div
              key={u._id}
              title={u.name}
              className="h-6 w-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600 ring-2 ring-white"
            >
              {getInitials(u.name)}
            </div>
          ))}
          {assignees.length > 3 && (
            <div className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-medium text-slate-500 ring-2 ring-white">
              +{assignees.length - 3}
            </div>
          )}
        </div>
      )}

      {/* Priority badge */}
      {task.priority && (
        <span
          className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${priorityColor(
            task.priority
          )}`}
        >
          {task.priority}
        </span>
      )}
    </div>
  );
};

export default RealEstateChecklistTab;
